import type { CSSProperties, FC, ReactNode } from 'react'
import type { AtlasRegion, AtlasViewKey, MapBounds } from '@/lib/atlas'
import type { Office, ProjectSummary } from '@/types'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { ATLAS_VIEWS, getDotField, project as toMap, regionOf } from '@/lib/atlas'
import { isMotionPaused, motionStore } from '@/lib/motion'
import { AtlasFacts } from '@/components/atlas/atlas-facts'

import { OFFICES } from '@/constants/navigation'

interface AtlasMapProps {
    projects: Array<ProjectSummary>
    idPrefix?: string
    isVisible?: (project: ProjectSummary) => boolean
    leftInset?: number
    panelExtra?: (project: ProjectSummary) => ReactNode
    stageClassName?: string
    children?: ReactNode
}

interface Leader {
    dx: number
    dy: number
}

interface Marker {
    key: string
    kind: 'office' | 'project'
    x: number
    y: number
    leader: Leader
    region: AtlasRegion
    project?: ProjectSummary
    office?: Office
    index: number
}

interface View {
    cx: number
    cy: number
    s: number
    ax: number
    ay: number
}

const LEADERS: Record<string, Leader> = {
    'kelda-bath-house': { dx: 30, dy: -30 },
    'hallmark-library': { dx: -30, dy: -30 },
    'vantaa-station-roof': { dx: 30, dy: -28 },
    'ostra-cultural-hall': { dx: 30, dy: 26 },
    'casa-mares': { dx: -32, dy: 28 },
    'ribeira-bridge-pavilion': { dx: 32, dy: -26 },
    'gowanus-archive': { dx: -32, dy: -28 },
    'riverline-market-hall': { dx: -30, dy: -28 },
    'alder-court-housing': { dx: 30, dy: -26 },
    'harbor-point-library': { dx: 30, dy: -28 }
}

const OFFICE_LEADERS: Record<string, Leader> = {
    STO: { dx: -34, dy: 22 },
    LIS: { dx: -34, dy: -26 },
    NYC: { dx: 34, dy: 24 }
}

type LabelMode = 'full' | 'city' | 'none'

const DEFAULT_LEADER: Leader = { dx: 30, dy: -26 }
const PANEL_WIDTH = 380
const FOCUS_SPAN = 14
const FOCUS_MIN = 1.6
const FRAME_MARGIN = 2.5
const FULL_LABELS = 14
const CITY_LABELS = 7
const COARSE_SPACING = 2.6
const ZOOM_MS = 1150
const HIDDEN_ALPHA = 0.1

/**
 * Labels thin out with scale (pixels per degree of latitude): city and project name up close, the
 * city alone at continent scale, and only the markers when the whole map is squeezed onto a phone.
 */
const labelMode = (scale: number): LabelMode => (scale >= FULL_LABELS ? 'full' : scale >= CITY_LABELS ? 'city' : 'none')

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

const allVisible = (): boolean => true

/**
 * A dotted map of Europe and the USA with both offices, every mapped project, dashed arcs from each
 * lead office, a switch between the All, Europe and USA frames, and an eased zoom into any project
 * with a facts panel beside it. Projects that are filtered out fade back and stop taking clicks.
 */
export const AtlasMap: FC<AtlasMapProps> = ({
    projects,
    idPrefix = 'atlas',
    isVisible = allVisible,
    leftInset = 0,
    panelExtra,
    stageClassName,
    children
}): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const markerRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
    const titleRef = useRef<HTMLHeadingElement>(null)
    const compactTitleRef = useRef<HTMLHeadingElement>(null)
    const [selected, setSelected] = useState<string | null>(null)
    const [zoomLabel, setZoomLabel] = useState('1.0')
    const [viewKey, setViewKey] = useState<AtlasViewKey>('all')
    const selectedRef = useRef<string | null>(null)
    const viewKeyRef = useRef<AtlasViewKey>('all')
    const requestRef = useRef<(instant?: boolean) => void>(() => undefined)
    const kickRef = useRef<() => void>(() => undefined)
    const visibleRef = useRef<Record<string, boolean>>({})
    const leftInsetRef = useRef(leftInset)

    const mapped = useMemo(() => projects.filter((item): item is ProjectSummary & { lat: number, lon: number } => item.lat !== null && item.lon !== null), [projects])

    const markers = useMemo<Array<Marker>>(() => {
        const offices: Array<Marker> = OFFICES.map((office, index) => {
            const [x, y] = toMap(office.lon, office.lat)

            return {
                key: `office-${office.code}`,
                kind: 'office',
                x,
                y,
                leader: OFFICE_LEADERS[office.code] ?? DEFAULT_LEADER,
                region: regionOf(office.lon),
                office,
                index
            }
        })

        const items: Array<Marker> = mapped.map(item => {
            const [x, y] = toMap(item.lon, item.lat)

            return {
                key: item.id,
                kind: 'project',
                x,
                y,
                leader: LEADERS[item.slug] ?? DEFAULT_LEADER,
                region: regionOf(item.lon),
                project: item,
                index: projects.findIndex(entry => entry.id === item.id)
            }
        })

        return [...offices, ...items]
    }, [mapped, projects])

    const visibility = useMemo(() => {
        const map: Record<string, boolean> = {}

        for (const item of mapped) map[item.id] = isVisible(item)

        return map
    }, [mapped, isVisible])

    const active = markers.find(marker => marker.key === selected)?.project ?? null
    const americanCount = mapped.filter(item => regionOf(item.lon) === 'usa').length
    const currentView = ATLAS_VIEWS.find(item => item.key === viewKey) ?? ATLAS_VIEWS[0]

    useEffect(() => {
        visibleRef.current = visibility
        kickRef.current()
    }, [visibility])

    useEffect(() => {
        leftInsetRef.current = leftInset
        requestRef.current(true)
    }, [leftInset])

    useEffect(() => {
        const stage = stageRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!stage || !canvas || !context) return

        const field = getDotField()
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const desk = window.matchMedia('(min-width: 56.25rem)')
        const arcAlpha: Record<string, number> = {}
        const layer = document.createElement('canvas')
        const layerContext = layer.getContext('2d')

        let width = 1
        let height = 1
        let dpr = 1
        let allScale = 1
        let layerKey = ''
        let labels: LabelMode | null = null
        let view: View = { cx: 0, cy: 0, s: 1, ax: 0, ay: 0 }
        let from: View = view
        let to: View = view
        let animStart = 0
        let animating = false
        let flow = 0
        let visible = false
        let running = false
        let raf = 0
        let last = 0

        const inset = (): number => (desk.matches ? leftInsetRef.current : 0)
        const padding = (): number => (width < 600 ? 12 : 40)

        const regionBounds = (key: AtlasViewKey): MapBounds => {
            const preset = ATLAS_VIEWS.find(item => item.key === key) ?? ATLAS_VIEWS[0]
            const bounds = { ...preset.bounds }

            for (const marker of markers) {
                if (key !== 'all' && marker.region !== key) continue

                bounds.x0 = Math.min(bounds.x0, marker.x - FRAME_MARGIN)
                bounds.x1 = Math.max(bounds.x1, marker.x + FRAME_MARGIN)
                bounds.y0 = Math.min(bounds.y0, marker.y - FRAME_MARGIN)
                bounds.y1 = Math.max(bounds.y1, marker.y + FRAME_MARGIN)
            }

            return bounds
        }

        const fit = (bounds: MapBounds): number => {
            const pad = padding()
            const available = width - inset()

            return Math.max(0.5, Math.min((available - pad * 2) / (bounds.x1 - bounds.x0), (height - pad * 2) / (bounds.y1 - bounds.y0)))
        }

        const restingView = (): View => {
            const key = selectedRef.current
            const marker = key ? markers.find(entry => entry.key === key) : undefined
            const left = inset()
            const bounds = regionBounds(viewKeyRef.current)
            const resting = fit(bounds)

            if (!marker) {
                return {
                    cx: (bounds.x0 + bounds.x1) / 2,
                    cy: (bounds.y0 + bounds.y1) / 2,
                    s: resting,
                    ax: left + (width - left) / 2,
                    ay: height / 2
                }
            }

            const panel = desk.matches ? PANEL_WIDTH : 0
            const pad = padding()
            const focus = Math.min(width - left - panel - pad * 2, height - pad * 2) / FOCUS_SPAN

            return { cx: marker.x, cy: marker.y, s: Math.max(resting * FOCUS_MIN, focus), ax: left + (width - left - panel) / 2, ay: height / 2 }
        }

        const toScreen = (x: number, y: number): [number, number] => [(x - view.cx) * view.s + view.ax, (y - view.cy) * view.s + view.ay]

        const settleArcs = (step: number): boolean => {
            let moving = false

            for (const marker of markers) {
                if (marker.kind !== 'project') continue

                const target = visibleRef.current[marker.key] === false ? HIDDEN_ALPHA : 1
                const current = arcAlpha[marker.key] ?? target
                const next = current + (target - current) * step

                arcAlpha[marker.key] = Math.abs(target - next) < 0.01 ? target : next

                if (arcAlpha[marker.key] !== target) moving = true
            }

            return moving
        }

        /**
         * The dots only change when the view or the selection does, so they are painted into an
         * offscreen layer and reused while only the dashed arcs flow.
         */
        const paintDots = (target: CanvasRenderingContext2D, focus: Marker | undefined): void => {
            target.setTransform(dpr, 0, 0, dpr, 0, 0)
            target.clearRect(0, 0, width, height)

            const coarse = field.step * view.s < COARSE_SPACING
            const points = coarse ? field.coarse : field.land
            const spacing = field.step * view.s * (coarse ? 2 : 1)
            const size = Math.min(4, Math.max(1.1, spacing * 0.24))
            const half = size / 2

            target.fillStyle = 'rgba(138, 132, 120, 0.22)'

            for (let i = 0; i < field.sea.length; i += 2) {
                const sx = (field.sea[i] - view.cx) * view.s + view.ax
                const sy = (field.sea[i + 1] - view.cy) * view.s + view.ay

                if (sx < -4 || sy < -4 || sx > width + 4 || sy > height + 4) continue

                target.fillRect(sx - 0.5, sy - 0.5, 1, 1)
            }

            target.fillStyle = 'rgba(216, 210, 196, 0.42)'

            for (let i = 0; i < points.length; i += 2) {
                const x = points[i]
                const y = points[i + 1]
                const sx = (x - view.cx) * view.s + view.ax
                const sy = (y - view.cy) * view.s + view.ay

                if (sx < -4 || sy < -4 || sx > width + 4 || sy > height + 4) continue

                if (focus && Math.hypot(x - focus.x, y - focus.y) < 1.8) continue

                target.fillRect(sx - half, sy - half, size, size)
            }

            if (focus) {
                target.fillStyle = 'rgba(236, 230, 216, 0.9)'

                for (let i = 0; i < points.length; i += 2) {
                    const x = points[i]
                    const y = points[i + 1]

                    if (Math.hypot(x - focus.x, y - focus.y) >= 1.8) continue

                    const [sx, sy] = toScreen(x, y)

                    target.fillRect(sx - half, sy - half, size, size)
                }
            }
        }

        const draw = (): void => {
            const key = selectedRef.current
            const focus = key ? markers.find(entry => entry.key === key) : undefined
            const mode = labelMode(view.s)

            if (mode !== labels) {
                labels = mode
                stage.dataset.atlasLabels = mode
            }

            if (layerContext) {
                const signature = `${view.cx}|${view.cy}|${view.s}|${view.ax}|${view.ay}|${layer.width}|${layer.height}|${key ?? ''}`

                if (signature !== layerKey) {
                    paintDots(layerContext, focus)
                    layerKey = signature
                }

                context.setTransform(1, 0, 0, 1, 0, 0)
                context.clearRect(0, 0, canvas.width, canvas.height)
                context.drawImage(layer, 0, 0)
            }
            else paintDots(context, focus)

            context.setTransform(dpr, 0, 0, dpr, 0, 0)
            context.lineWidth = 1
            context.setLineDash([4, 6])
            context.lineDashOffset = -flow

            for (const marker of markers) {
                if (marker.kind !== 'project' || !marker.project) continue

                const office = markers.find(entry => entry.kind === 'office' && entry.office?.code === marker.project?.leadOffice)

                if (!office) continue

                const dx = marker.x - office.x
                const dy = marker.y - office.y
                const length = Math.hypot(dx, dy)

                if (length < 0.8) continue

                let nx = -dy / length
                let ny = dx / length

                if (ny > 0) {
                    nx = -nx
                    ny = -ny
                }

                const alpha = (arcAlpha[marker.key] ?? 1) * (marker.key === key ? 1 : 0.62)
                const [ox, oy] = toScreen(office.x, office.y)
                const [px, py] = toScreen(marker.x, marker.y)
                const [qx, qy] = toScreen((office.x + marker.x) / 2 + nx * length * 0.26, (office.y + marker.y) / 2 + ny * length * 0.26)

                context.strokeStyle = `rgba(134, 182, 240, ${alpha.toFixed(3)})`
                context.beginPath()
                context.moveTo(ox, oy)
                context.quadraticCurveTo(qx, qy, px, py)
                context.stroke()
            }

            context.setLineDash([])

            for (const marker of markers) {
                const [sx, sy] = toScreen(marker.x, marker.y)
                const element = markerRefs.current[marker.key]
                const { dx, dy } = marker.leader
                const tail = dx >= 0 ? 12 : -12
                const alpha = marker.kind === 'project' ? (arcAlpha[marker.key] ?? 1) : 1

                if (element) element.style.transform = `translate3d(${sx.toFixed(1)}px, ${sy.toFixed(1)}px, 0)`

                if (marker.kind === 'project' && mode === 'none' && marker.key !== key) continue

                context.strokeStyle = `rgba(236, 230, 216, ${(0.38 * alpha).toFixed(3)})`
                context.beginPath()
                context.moveTo(sx + Math.sign(dx) * 5, sy + Math.sign(dy) * 5)
                context.lineTo(sx + dx, sy + dy)
                context.lineTo(sx + dx + tail, sy + dy)
                context.stroke()
            }
        }

        const kick = (): void => {
            if (running || !visible || document.hidden) return

            running = true
            last = performance.now()
            raf = requestAnimationFrame(loop)
        }

        const loop = (now: number): void => {
            if (!running) return

            const dt = Math.min(0.05, (now - last) / 1000)
            const still = reduced || isMotionPaused()

            last = now

            if (animating) {
                const t = Math.min(1, (now - animStart) / ZOOM_MS)
                const e = easeInOutCubic(t)

                view = {
                    cx: from.cx + (to.cx - from.cx) * e,
                    cy: from.cy + (to.cy - from.cy) * e,
                    s: from.s * Math.pow(to.s / from.s, e),
                    ax: from.ax + (to.ax - from.ax) * e,
                    ay: from.ay + (to.ay - from.ay) * e
                }

                if (t >= 1) {
                    animating = false
                    view = to
                }
            }

            const fading = settleArcs(still ? 1 : 1 - Math.exp(-dt * 8))

            if (!still) flow += dt * 9

            draw()

            if (animating || fading || !still) raf = requestAnimationFrame(loop)
            else running = false
        }

        const retarget = (instant: boolean): void => {
            const next = restingView()

            if (instant || reduced || isMotionPaused()) {
                animating = false
                view = next
                settleArcs(1)
                draw()
                setZoomLabel((next.s / allScale).toFixed(1))

                return
            }

            from = view
            to = next
            animStart = performance.now()
            animating = true
            setZoomLabel((next.s / allScale).toFixed(1))
            kick()
        }

        const resize = (): void => {
            const rect = stage.getBoundingClientRect()

            width = Math.max(1, Math.round(rect.width))
            height = Math.max(1, Math.round(rect.height))
            dpr = Math.min(window.devicePixelRatio || 1, 2)
            canvas.width = Math.round(width * dpr)
            canvas.height = Math.round(height * dpr)
            layer.width = canvas.width
            layer.height = canvas.height
            layerKey = ''
            allScale = fit(regionBounds('all'))
            retarget(true)
        }

        requestRef.current = (instant?: boolean) => {
            if (instant) resize()
            else retarget(false)
        }
        kickRef.current = () => {
            if (reduced || isMotionPaused()) {
                settleArcs(1)
                draw()

                return
            }

            kick()
        }

        settleArcs(1)

        const resizeObserver = new ResizeObserver(resize)
        const intersectionObserver = new IntersectionObserver(entries => {
            visible = entries.some(entry => entry.isIntersecting)

            if (visible) kick()
            else {
                running = false
                cancelAnimationFrame(raf)
            }
        })
        const onVisibility = (): void => {
            if (!document.hidden) kick()
        }
        const unsubscribe = motionStore.subscribe(kick)

        resize()
        resizeObserver.observe(stage)
        intersectionObserver.observe(stage)
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            resizeObserver.disconnect()
            intersectionObserver.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
            unsubscribe()
            requestRef.current = () => undefined
            kickRef.current = () => undefined
        }
    }, [markers])

    const chooseView = useCallback((key: AtlasViewKey): void => {
        viewKeyRef.current = key
        selectedRef.current = null
        setViewKey(key)
        setSelected(null)
        requestRef.current()
    }, [])

    const select = useCallback((key: string | null): void => {
        const previous = selectedRef.current

        selectedRef.current = key
        setSelected(key)
        requestRef.current()

        if (key === null && previous) buttonRefs.current[previous]?.focus({ preventScroll: true })
    }, [])

    useEffect(() => {
        if (selected && visibility[selected] === false) select(null)
    }, [selected, visibility, select])

    useEffect(() => {
        if (!selected) return

        if (window.matchMedia('(min-width: 56.25rem)').matches) titleRef.current?.focus({ preventScroll: true })
        else compactTitleRef.current?.focus()

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') select(null)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [selected, select])

    const panelId = `${idPrefix}-panel`
    const compactId = `${idPrefix}-panel-compact`
    const activeIndex = markers.find(marker => marker.key === selected)?.index ?? 0

    return (
        <div className='relative'>
            <div
                ref={stageRef}
                data-crosshair=''
                role='group'
                aria-label={`Dotted map of Europe and the USA, framed on ${currentView.name}, with the Stockholm, Lisbon and New York offices and ${mapped.length} project markers. Each project is linked to its lead office by a dashed arc.`}
                data-atlas-labels='city'
                onPointerDown={event => {
                    if (event.target === event.currentTarget || event.target === canvasRef.current) select(null)
                }}
                className={cn('relative w-full overflow-hidden bg-background', stageClassName)}
            >
                <canvas ref={canvasRef} className='absolute inset-0 size-full' aria-hidden='true' />

                <div className='pointer-events-none absolute inset-0'>
                    {markers.map(marker => {
                        const { dx, dy } = marker.leader
                        const labelStyle: CSSProperties = {
                            left: dx + (dx >= 0 ? 16 : -16),
                            top: dy,
                            transform: dx >= 0 ? 'translate(0, -50%)' : 'translate(-100%, -50%)'
                        }

                        if (marker.kind === 'office' && marker.office) {
                            return (
                                <div
                                    key={marker.key}
                                    ref={element => {
                                        markerRefs.current[marker.key] = element
                                    }}
                                    className='absolute left-0 top-0'
                                >
                                    <span className='absolute -left-[6px] -top-[6px] size-3 border border-signal bg-background' aria-hidden='true' />

                                    <span className={cn('hud absolute whitespace-nowrap', dx < 0 && 'text-right')} style={labelStyle}>
                                        <span className='block text-ink'>
                                            {marker.office.city}
                                        </span>

                                        <span className='block text-mute'>
                                            Office / {marker.office.code}
                                        </span>
                                    </span>
                                </div>
                            )
                        }

                        const item = marker.project as ProjectSummary
                        const isSelected = selected === marker.key
                        const shown = visibility[item.id] !== false

                        return (
                            <div
                                key={marker.key}
                                ref={element => {
                                    markerRefs.current[marker.key] = element
                                }}
                                className={cn('absolute left-0 top-0 transition-opacity duration-500', shown ? 'opacity-100' : 'opacity-15')}
                            >
                                <button
                                    ref={element => {
                                        buttonRefs.current[marker.key] = element
                                    }}
                                    type='button'
                                    aria-pressed={isSelected}
                                    aria-controls={`${panelId} ${compactId}`}
                                    disabled={!shown}
                                    tabIndex={shown ? 0 : -1}
                                    onClick={() => select(isSelected ? null : marker.key)}
                                    className={cn('absolute -left-3 -top-3 grid size-6 place-items-center', shown ? 'pointer-events-auto' : 'pointer-events-none')}
                                >
                                    <span className={cn('size-2 bg-signal transition-transform duration-300', isSelected && 'scale-125')} aria-hidden='true' />

                                    {isSelected && <span className='absolute inset-0.5 border border-signal' aria-hidden='true' />}

                                    <span className='sr-only'>
                                        {isSelected ? 'Close facts for ' : 'Show facts for '}
                                        {item.name}, {item.city}
                                    </span>
                                </button>

                                <span className={cn('atlas-project-label hud absolute whitespace-nowrap transition-opacity duration-300', dx < 0 && 'text-right', selected && !isSelected && 'opacity-40')} style={labelStyle} aria-hidden='true'>
                                    <span className='block text-ink' data-mdlfy-cms={`projects:${item.id}:city`}>
                                        {item.city}
                                    </span>

                                    <span className='atlas-name hidden text-mute sm:block' data-mdlfy-cms={`projects:${item.id}:name`}>
                                        {item.name}
                                    </span>
                                </span>
                            </div>
                        )
                    })}
                </div>

                {children}

                <div role='group' aria-label='Map view' className={cn('absolute right-gutter top-5 z-[5] flex border border-hairline bg-background', active && 'desk:mr-[380px]')}>
                    {ATLAS_VIEWS.map((item, index) => (
                        <button
                            key={item.key}
                            type='button'
                            aria-pressed={viewKey === item.key}
                            onClick={() => chooseView(item.key)}
                            className={cn(
                                'hud h-9 px-3 transition-colors duration-300',
                                index > 0 && 'border-l border-hairline',
                                viewKey === item.key ? 'bg-ink text-background' : 'text-mute hover:text-ink'
                            )}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <p role='status' className={cn('hud pointer-events-none absolute right-gutter top-[68px] z-[5] text-right text-mute', active && 'desk:mr-[380px]')}>
                    {viewKey === 'usa' && !active && americanCount === 0 ? 'No projects mapped in the USA yet' : ''}
                </p>

                <div className='pointer-events-none absolute inset-x-gutter bottom-5 flex flex-wrap items-end justify-end gap-4' aria-hidden='true'>
                    <div className={cn('mr-auto flex flex-wrap items-center gap-x-5 gap-y-2', leftInset > 0 && 'desk:invisible')}>
                        <span className='hud flex items-center gap-2 text-mute'>
                            <span className='size-2.5 border border-signal' />
                            Office
                        </span>

                        <span className='hud flex items-center gap-2 text-mute'>
                            <span className='size-2 bg-signal' />
                            Project
                        </span>

                        <span className='hud flex items-center gap-2 text-mute'>
                            <span className='w-5 border-t border-dashed border-signal' />
                            Lead office
                        </span>
                    </div>

                    <span className={cn('hud text-mute', active && 'desk:mr-[380px]')}>
                        Zoom {zoomLabel}×
                    </span>
                </div>

                {active && (
                    <aside
                        id={panelId}
                        aria-labelledby={`${panelId}-title`}
                        className='absolute inset-y-0 right-0 z-10 hidden w-[380px] flex-col border-l border-hairline bg-panel animate-in fade-in slide-in-from-right-8 duration-500 desk:flex'
                    >
                        <AtlasFacts project={active} index={activeIndex} titleId={`${panelId}-title`} titleRef={titleRef} onClose={() => select(null)} extra={panelExtra?.(active)} />
                    </aside>
                )}
            </div>

            {active && (
                <div id={compactId} aria-labelledby={`${compactId}-title`} role='region' className='border-t border-hairline bg-panel desk:hidden'>
                    <AtlasFacts project={active} index={activeIndex} titleId={`${compactId}-title`} titleRef={compactTitleRef} onClose={() => select(null)} extra={panelExtra?.(active)} />
                </div>
            )}
        </div>
    )
}
